import { useCallback, useEffect, useRef, useState } from 'react'

import type {
    StationNewOrderEvent,
    StationNotificationEvent,
} from './use-station-sse'

import {
    createTtsKeepalive,
    pickVietnameseVoice,
    playBeepChime,
    renderSpeechTemplate,
    warmUpTts,
} from '@/lib/station-tts'
import { m } from '@/lib/paraglide/messages'

const SOUND_PREF_KEY = 'station.soundEnabled'
const AUDIO_BLOCKED_PROBE_MS = 300

function readStoredPreference(): boolean {
    if (typeof window === 'undefined') return false
    try {
        return window.localStorage.getItem(SOUND_PREF_KEY) === '1'
    } catch {
        return false
    }
}

/**
 * Audio notification hook for the packing station.
 * Uses TTS (Web Speech API) with fallback to a Web Audio chime.
 * Preference persists in localStorage so users don't re-enable on every reload.
 *
 * Hardening notes:
 *   - `audioBlocked` reflects whether the last utterance fired `onstart`. The
 *     UI uses it to switch from "sound on" to a "tap to enable" prompt when
 *     the browser silently autoplay-locked us (typical after a reload).
 *   - A keepalive timer fires `pause(); resume()` every 10s while sound is on
 *     so Chrome doesn't garbage-collect the queue on long-running tablets.
 *   - We do NOT call `speechSynthesis.cancel()` before every `speak()` —
 *     it caused a race that swallowed utterances on bursts.
 */
export function useStationAudio() {
    const [soundEnabled, setSoundEnabled] = useState(readStoredPreference)
    const [audioBlocked, setAudioBlocked] = useState(false)
    const viVoiceRef = useRef<SpeechSynthesisVoice | null>(null)
    const probeTimerRef = useRef<number | null>(null)

    // Warm-up here is intentional: when the page mounts with the persisted
    // preference already enabled, the warm-up call runs outside a user
    // gesture, which Chrome blocks. That blocked attempt is what trips the
    // 300ms probe in speakOrChime → setAudioBlocked(true) → the amber
    // "tap to enable" prompt. Removing this effect would silently break
    // that recovery flow.
    useEffect(() => {
        if (soundEnabled) warmUpTts()
    }, [soundEnabled])

    useEffect(() => {
        if (!('speechSynthesis' in window)) return
        const resolve = () => {
            viVoiceRef.current = pickVietnameseVoice()
        }
        resolve()
        speechSynthesis.addEventListener('voiceschanged', resolve)
        return () => {
            speechSynthesis.removeEventListener('voiceschanged', resolve)
        }
    }, [])

    // Keepalive: only while the user has explicitly enabled sound.
    useEffect(() => {
        if (!soundEnabled) return
        const ka = createTtsKeepalive()
        ka.start()
        return () => ka.stop()
    }, [soundEnabled])

    // When audio is locked, a single document-level pointerdown lets the user
    // re-arm playback. The listener removes itself once fired.
    useEffect(() => {
        if (!audioBlocked || typeof document === 'undefined') return
        const unlock = () => {
            warmUpTts()
            setAudioBlocked(false)
        }
        document.addEventListener('pointerdown', unlock, { once: true })
        return () => document.removeEventListener('pointerdown', unlock)
    }, [audioBlocked])

    const clearProbeTimer = useCallback(() => {
        if (probeTimerRef.current != null) {
            window.clearTimeout(probeTimerRef.current)
            probeTimerRef.current = null
        }
    }, [])

    // Tear down any in-flight probe on unmount so it doesn't setState on a
    // dead component.
    useEffect(() => clearProbeTimer, [clearProbeTimer])

    const enableSound = useCallback(() => {
        setSoundEnabled(true)
        try {
            window.localStorage.setItem(SOUND_PREF_KEY, '1')
        } catch {
            /* storage unavailable — preference won't persist this session */
        }
        // The mount effect (above) already calls warmUpTts when soundEnabled
        // flips true, so we don't double-up here.
        setAudioBlocked(false)
    }, [])

    const disableSound = useCallback(() => {
        setSoundEnabled(false)
        try {
            window.localStorage.setItem(SOUND_PREF_KEY, '0')
        } catch {
            /* storage unavailable */
        }
        if ('speechSynthesis' in window) {
            speechSynthesis.cancel()
        }
        // Cancel above guarantees the in-flight utterance won't fire onstart;
        // clear the pending probe so it doesn't set audioBlocked=true after
        // the user already turned sound off.
        clearProbeTimer()
        setAudioBlocked(false)
    }, [clearProbeTimer])

    const speakOrChime = useCallback(
        (text: string) => {
            if (!soundEnabled || !text) return
            const viVoice = viVoiceRef.current ?? pickVietnameseVoice()
            if ('speechSynthesis' in window && viVoice) {
                // The utterance setters type-check natively (a stale/foreign
                // voice object throws). Fall through to the chime instead of
                // letting the error escape into the SSE handler, which would
                // also kill the visual toast for the same event.
                try {
                    viVoiceRef.current = viVoice
                    const utterance = new SpeechSynthesisUtterance(text)
                    utterance.voice = viVoice
                    utterance.lang = viVoice.lang
                    utterance.rate = 1.0
                    utterance.volume = 1.0
                    let fired = false
                    utterance.onstart = () => {
                        fired = true
                        setAudioBlocked(false)
                    }
                    speechSynthesis.speak(utterance)
                    clearProbeTimer()
                    probeTimerRef.current = window.setTimeout(() => {
                        probeTimerRef.current = null
                        if (!fired) setAudioBlocked(true)
                    }, AUDIO_BLOCKED_PROBE_MS)
                    return
                } catch {
                    viVoiceRef.current = null
                }
            }
            playBeepChime()
        },
        [soundEnabled, clearProbeTimer],
    )

    const playNewOrderAlert = useCallback(
        (event: StationNewOrderEvent) => {
            if (!event.speech_template) return
            speakOrChime(
                renderSpeechTemplate(event.speech_template, {
                    ma_don: event.order_code,
                    ma_don_noi_bo: event.internal_order_code,
                    ten_khach: event.customer_name,
                    sdt: event.customer_phone,
                    so_tien: event.amount,
                    nguoi_tao: event.created_by_name,
                    nguoi_giao: event.driver_assignee_name,
                }),
            )
        },
        [speakOrChime],
    )

    const playNotificationAlert = useCallback(
        (event: StationNotificationEvent) => {
            if (!event.speech_template) return
            speakOrChime(
                renderSpeechTemplate(event.speech_template, {
                    ma_don: event.payload?.order_code,
                    ma_don_noi_bo: event.payload?.internal_order_code,
                    ten_khach: event.payload?.customer_name,
                    sdt: event.payload?.customer_phone,
                    so_tien: event.payload?.amount,
                    nguoi_tao: event.payload?.created_by_name,
                    nguoi_giao: event.payload?.driver_assignee_name,
                }),
            )
        },
        [speakOrChime],
    )

    // Manual test utterance for the on-screen "Thử loa" button: lets staff
    // (or remote support) split device-side audio problems (media volume,
    // missing Vietnamese TTS voice, autoplay lock) from missing SSE events
    // without creating a real order.
    const playTestSound = useCallback(() => {
        speakOrChime(m.station_test_sound_message())
    }, [speakOrChime])

    return {
        soundEnabled,
        audioBlocked,
        enableSound,
        disableSound,
        playNewOrderAlert,
        playNotificationAlert,
        playTestSound,
    }
}
